import {pipe} from '@packages/utilities/fp/utils';
import {dissoc} from '@packages/utilities/key-map';
import {Type, Types, Expressions} from '@packages/systems/wfdl';
import {Expression, ERecord} from '@packages/systems/wfdl-ast';
import {migrateRecordDataType} from './migrateFromRecordType';
import {migrateUnionDataType} from './migrateFromUnionType';
import {migrateListDataType} from './migrateFromListType';
import {migratePluginDataType, migrateLiteral} from './migrateFromPluginType';
import {migrateGenericTypeStructure} from './migrateGenericTypeStructure';
import {migrateChildrenDataTypeToListOfElement} from './migrateChildrenDataTypeToListOfElement';
import {migrateTextDataType} from './migrateFromTextType';
import {migrateBooleanDataType} from './migrateFromBooleanType';
import {migrateEnumDataType} from './migrateFromEnumType';
import {migrateNumberDataType} from './migrateFromNumberType';
import {migrateReferenceTypeStructure} from './migrateReferenceTypeStructure';
import {migrateDevlinkRuntimePropsDataType} from './migrateFromDevlinkRuntimeProps';
import {migrateLineBreakSystemDefaultValue} from './migrateLineBreakSystemDefaultValue';

export {migrateLiteral};

/**
 * Removes overrides for which the type does not match what is definined on
 * the component, including overrides for fields that no longer exist.
 *
 * These mismatched overrides exist because we used to allow changing the type
 * of a field in an early iteration of user components.
 *
 * This function is used as a reification patch, but instead of working with
 * the untyped JsonNode data it gets to work with the safer Type and Expression.
 */
export const removeMismatchOverrides = (
  instanceDataType: Type,
  dataType: Type,
  data: Expression
): Expression => {
  const fields = Types.getRecordFields(dataType);
  const instFields = Types.getRecordFields(instanceDataType);
  const overrides = Expressions.getRecord(data);
  if (!fields || !instFields || !overrides) return data;

  const newOverrides = Object.entries(instFields).reduce(
    (acc, [fieldName, instField]) => {
      const field = fields[fieldName];

      if (!field || !Types.equal(instField, field)) {
        return dissoc(fieldName)(acc);
      }

      return acc;
    },
    overrides
  );

  return newOverrides === overrides ? data : ERecord(newOverrides);
};

const _fixDataTypeJson = (dataTypeJson: any) => {
  if (!dataTypeJson) return dataTypeJson;

  if (dataTypeJson.type === 'TypeApplication') {
    return {
      ...dataTypeJson,
      val: {
        con: fixDataTypeJson(dataTypeJson.val.con),
        arg: fixDataTypeJson(dataTypeJson.val.arg),
      },
    };
  }

  if (dataTypeJson.type === 'RowCons') {
    return {
      ...dataTypeJson,
      val: {
        label: dataTypeJson.val.label,
        type: fixDataTypeJson(dataTypeJson.val.type),
        tail: fixDataTypeJson(dataTypeJson.val.tail),
      },
    };
  }

  if (dataTypeJson.type === 'Generic') {
    return {
      ...dataTypeJson,
      val: {
        arg: dataTypeJson.val.arg,
        body: fixDataTypeJson(dataTypeJson.val.body),
      },
    };
  }

  return dataTypeJson;
};

export const fixDataTypeJson = pipe([
  migrateRecordDataType,
  migrateUnionDataType,
  migrateListDataType,
  migrateGenericTypeStructure,
  _fixDataTypeJson,
  migrateChildrenDataTypeToListOfElement,
  migratePluginDataType,
  migrateTextDataType,
  migrateBooleanDataType,
  migrateEnumDataType,
  migrateNumberDataType,
  migrateReferenceTypeStructure,
  migrateDevlinkRuntimePropsDataType,
  migrateLineBreakSystemDefaultValue,
]);
